import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import Button, { ButtonProps } from "./Button";
import AIIcon from "../img/AI.svg";

const TAPPED_KEY_PREFIX = "ai-button-tapped:";
const TOOLTIP_KEY_PREFIX = "ai-tooltip-shown:";
const TOOLTIP_AUTO_DISMISS_MS = 3000;

interface Props extends Omit<ButtonProps, "icon"> {
    aiButtonId: string;
    icon?: string;
    enabled?: boolean;
    tooltipText?: string;
}

const AIButton = (props: Props) => {
    const {
        aiButtonId,
        icon = AIIcon,
        enabled = true,
        tooltipText = "Tap — AI will do it for you ✨",
        onClick,
        className,
        ...buttonProps
    } = props;

    const tappedKey = `${TAPPED_KEY_PREFIX}${aiButtonId}`;
    const tooltipKey = `${TOOLTIP_KEY_PREFIX}${aiButtonId}`;

    const [tapped, setTapped] = useState(
        () => typeof window !== "undefined" && !!localStorage.getItem(tappedKey)
    );
    const [pulsing, setPulsing] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const timeoutsRef = useRef<{
        animation?: ReturnType<typeof setTimeout>;
        cycle?: ReturnType<typeof setInterval>;
        tooltip?: ReturnType<typeof setTimeout>;
    }>({});

    useEffect(() => {
        if (tapped || !enabled) return;

        const startCycle = () => {
            setPulsing(true);
            timeoutsRef.current.animation = setTimeout(() => {
                setPulsing(false);
            }, 4500);

            timeoutsRef.current.cycle = setInterval(() => {
                setPulsing(true);
                timeoutsRef.current.animation = setTimeout(() => {
                    setPulsing(false);
                }, 4500);
            }, 8000);
        };

        startCycle();

        const timeouts = timeoutsRef.current;
        return () => {
            if (timeouts.animation) clearTimeout(timeouts.animation);
            if (timeouts.cycle) clearInterval(timeouts.cycle);
            setPulsing(false);
        };
    }, [tapped, enabled]);

    useEffect(() => {
        if (tapped || !enabled) return;
        if (typeof window === "undefined") return;
        if (localStorage.getItem(tooltipKey)) return;

        setTooltipOpen(true);
        localStorage.setItem(tooltipKey, "true");
        timeoutsRef.current.tooltip = setTimeout(() => {
            setTooltipOpen(false);
        }, TOOLTIP_AUTO_DISMISS_MS);

        const timeouts = timeoutsRef.current;
        return () => {
            if (timeouts.tooltip) clearTimeout(timeouts.tooltip);
        };
    }, [tapped, enabled, tooltipKey]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!tapped) {
            try {
                localStorage.setItem(tappedKey, "true");
            } catch {
                // localStorage may be unavailable (private mode etc.) — ignore
            }
            setTapped(true);
        }
        setPulsing(false);
        setTooltipOpen(false);
        onClick?.(e);
    };

    const composedClassName = [pulsing ? "idle-pulse" : "", className]
        .filter(Boolean)
        .join(" ");

    const button = (
        <Button
            {...buttonProps}
            icon={icon}
            color={buttonProps.color ?? "primary"}
            onClick={handleClick}
            className={composedClassName || undefined}
        />
    );

    if (!enabled || tapped) return button;

    return (
        <Tooltip
            open={tooltipOpen}
            title={tooltipText}
            arrow
            placement="top"
            disableHoverListener
            disableFocusListener
            disableTouchListener
            PopperProps={{ disablePortal: true }}
        >
            <span style={{ display: "inline-flex" }}>{button}</span>
        </Tooltip>
    );
};

export default AIButton;
